/**
 * Translation dictionary for MotorScan.
 *
 * Conventions:
 *   - Keys are flat, dotted, namespaced by surface: `landing.hero.title`,
 *     `nav.scans`, `auth.signin.subject`, etc.
 *   - Values use `{name}` for interpolation. No ICU, no pluralization yet —
 *     when we need it we'll use `Intl.PluralRules` natively per call site.
 *   - The FR object is the SOURCE OF TRUTH. The EN type is derived from FR,
 *     so the compiler tells us when a key is missing in EN.
 *   - We do NOT lazily fetch — the whole dict is bundled in. It's tiny (~10kb
 *     per locale uncompressed) and we render server-side, so DRY-server-state
 *     is more important than a half-loaded translation file.
 */

export const dictFr = {
  // ─── Common UI atoms ────────────────────────────────────────────────────
  "common.loading": "Chargement…",
  "common.error": "Une erreur est survenue.",
  "common.retry": "Réessayer",
  "common.cancel": "Annuler",
  "common.save": "Enregistrer",
  "common.close": "Fermer",
  "common.back": "Retour",
  "common.next": "Suivant",
  "common.signin": "Connexion",
  "common.signout": "Déconnexion",
  "common.signup_free": "Essayer gratuitement",
  "common.startScan": "Lancer un scan",
  "common.startScanFree": "Lancer un scan gratuit",
  "common.viewListing": "Voir l'annonce",
  "common.view": "Voir →",
  "common.connecting": "Connexion…",
  "common.sending": "Envoi…",

  // ─── Header / footer / nav ──────────────────────────────────────────────
  "nav.scans": "Mes scans",
  "nav.newScan": "Nouveau scan",
  "nav.market": "Marché",
  "nav.account": "Compte",
  "footer.tagline": "Le scanner européen d'annonces de voitures. Une seule voiture, surveillée 24/7.",
  "footer.product": "Produit",
  "footer.market": "Marché",
  "footer.legal": "Légal",
  "footer.legalMentions": "Mentions légales",
  "footer.cgu": "CGU",
  "footer.app": "L'application",
  "footer.copyright": "© {year} MotorScan · Données agrégées depuis des sites publics d'annonces · Pas affilié aux marques mentionnées.",

  // ─── Locale switcher ────────────────────────────────────────────────────
  "locale.fr": "Français",
  "locale.en": "English",

  // ─── Landing page ───────────────────────────────────────────────────────
  "landing.kicker": "MotorScan · scanner d'annonces de voitures · 12 sites · 5 pays",
  "landing.hero.title": "On scanne le marché européen pour la voiture que vous voulez.",
  "landing.hero.subtitle":
    "Décrivez la voiture, le budget, et la zone. Toutes les heures, on regarde LeBonCoin, La Centrale, Mobile.de, AutoScout24, et 8 autres sites. On vous notifie quand une vraie affaire apparaît — pas une promo, pas un coup marketing, juste un prix réellement inférieur au marché.",
  "landing.hero.exploreMarket": "Explorer le marché",
  "landing.demo.kicker": "Démo live · scan Ferrari 458 · données réelles",
  "landing.demo.deals": "{n} affaires",
  "landing.demo.col.deal": "Deal",
  "landing.demo.col.listing": "Annonce",
  "landing.demo.col.year": "Année",
  "landing.demo.col.km": "Km",
  "landing.demo.col.price": "Prix",
  "landing.demo.col.savings": "Économie",
  "landing.demo.col.seen": "Vu",
  "landing.how.kicker": "Comment ça marche",
  "landing.how.title": "Trois étapes. Puis vous attendez que ça pingue.",
  "landing.how.step1.h": "Vous définissez votre cible",
  "landing.how.step1.p":
    "Marque, modèle, année, budget, kilométrage, pays à scanner. Précis comme une recherche La Centrale, sauf que vous le faites une seule fois.",
  "landing.how.step2.h": "On scanne en continu",
  "landing.how.step2.p":
    "Toutes les heures, 12 sites européens. LeBonCoin, La Centrale, AutoScout24, Mobile.de, Subito, AutoTrader, Coches.net, et 5 autres. Aucun site échappe au scanner.",
  "landing.how.step3.h": "On vous prévient au bon moment",
  "landing.how.step3.p":
    "Push, email, ou Telegram. Uniquement quand une annonce dépasse votre seuil de Deal Rating. Pas de spam. Pas de notification de complaisance.",
  "landing.fairprice.kicker": "Le Fair Price",
  "landing.fairprice.title": "Un prix juste, calculé sur 30 jours d'observations réelles.",
  "landing.fairprice.body1":
    "On agrège chaque annonce vue ces 30 derniers jours par (marque, modèle, version, pays, année) et on calcule la médiane, les percentiles 10 et 90, et l'effet kilométrage. Le résultat : un prix juste par voiture, mis à jour toutes les 30 minutes.",
  "landing.fairprice.body2":
    "Quand l'échantillon est trop fin (moins de 5 annonces sur la cible exacte), on élargit à année ± 1, puis à tous pays — et on affiche honnêtement le niveau de confiance. Aucune valeur inventée.",
  "landing.fairprice.levels": "Les 5 niveaux",
  "landing.fairprice.super": "≤ p10 · sous le 10ᵉ percentile du marché",
  "landing.fairprice.good": "> p10, ≤ médiane · bonne affaire raisonnable",
  "landing.fairprice.fair": "autour du juste prix",
  "landing.fairprice.high": "au-dessus du marché",
  "landing.fairprice.over": "> p90 · prix manifestement excessif",
  "landing.pricing.kicker": "Tarifs",
  "landing.pricing.title": "Hebdomadaire. Pause à tout moment.",
  "landing.pricing.intro":
    "Vous achèterez votre voiture dans 2 ou 3 semaines. Pourquoi payer un mois entier ? La pause est en un clic. Annulez quand votre voiture est trouvée.",
  "landing.pricing.unitWeek": "€ / sem.",
  "landing.pricing.unitMonth": "€ / mois",
  "landing.pricing.free.tagline": "Découvrez. 1 scan actif, alertes mail quotidiennes.",
  "landing.pricing.hunter.tagline": "3 scans, push + email, filtre Super Price.",
  "landing.pricing.hunterPro.tagline": "10 scans, Telegram, rapports hebdo, comparateur.",
  "landing.pricing.agency.tagline": "Illimité, Slack/webhook, CSV, multi-utilisateurs.",
  "landing.pricing.free.cta": "Commencer",
  "landing.pricing.hunter.cta": "Choisir Hunter",
  "landing.pricing.hunterPro.cta": "Choisir Pro",
  "landing.pricing.agency.cta": "Nous contacter",
  "landing.pricing.disclaimer":
    "Le moyen de paiement sera annoncé sous peu. Les comptes Free sont disponibles dès maintenant.",
  "landing.faq.kicker": "FAQ",
  "landing.faq.title": "Les questions qu'on nous pose le plus",
  "landing.faq.q1": "Vous vendez des voitures ?",
  "landing.faq.a1":
    "Non. MotorScan ne vend rien. On vous envoie vers les annonces sources (LeBonCoin, La Centrale, etc.). On ne touche jamais la transaction.",
  "landing.faq.q2": "Quels sites sont scannés ?",
  "landing.faq.a2":
    "Aujourd'hui : LeBonCoin, La Centrale, AutoScout24 (FR/DE), Mobile.de, Subito, Comparis, Coches.net, Tweedehands, LeParking, AutoTrader, plus quelques agrégateurs. Toutes les heures.",
  "landing.faq.q3": "Vous avez vraiment 30 jours d'historique ?",
  "landing.faq.a3":
    "Oui, et plus. On stocke chaque observation (annonce + prix + km + statut) dans une base temporelle. C'est ce qui permet les courbes d'évolution et le Fair Price.",
  "landing.faq.q5": "C'est légal de scanner les sites ?",
  "landing.faq.a5":
    "On n'aspire pas les annonces pour les republier. On notifie l'utilisateur final, qui clique vers l'annonce source. C'est le même usage qu'un humain qui rafraîchit son onglet.",

  // ─── Auth ──────────────────────────────────────────────────────────────
  "auth.signin.title": "Connectez-vous par email",
  "auth.signin.subtitle":
    "Pas de mot de passe. On vous envoie un code à 6 chiffres par email.",
  "auth.signin.emailLabel": "Email",
  "auth.signin.cta": "Recevoir le code",
  "auth.signin.sentTitle": "Entrez votre code.",
  "auth.signin.sentBody":
    "Un code à 6 chiffres vient d'être envoyé à {email}.",
  "auth.signin.otpLabel": "Code à 6 chiffres",
  "auth.signin.otpCta": "Se connecter",
  "auth.signin.otpError": "Code invalide ou expiré. Réessayez.",
  "auth.signin.resend": "Renvoyer un code",
  "auth.signin.error": "Impossible d'envoyer. Vérifiez l'email et réessayez.",
  "auth.signin.placeholder": "vous@example.com",
  "auth.email.subject": "Votre code MotorScan : {code}",
  "auth.email.intro": "Bienvenue chez MotorScan.",
  "auth.email.body": "Voici votre code de connexion, valable {minutes} minutes :",
  "auth.email.cta": "Se connecter",
  "auth.email.fallbackLabel": "Ou cliquez sur le bouton ci-dessous :",
  "auth.email.footer": "Si vous n'êtes pas à l'origine de cette demande, ignorez simplement cet email.",
  "auth.email.textIntro": "Bienvenue.",
  "auth.email.textOtp": "Votre code de connexion : {code} (valide {minutes} min)",
  "auth.email.textFallback": "Ou cliquez sur ce lien :",
  "auth.email.textBody": "Cliquez ici pour vous connecter (lien valide {minutes} min) :",
  "auth.email.textFooter": "Si vous n'êtes pas à l'origine de cette demande, ignorez cet email.",
  "auth.fail.kicker": "Connexion",
  "auth.fail.title": "Impossible.",
  "auth.fail.message": "Lien invalide ou expiré. Demandez-en un nouveau.",
  "auth.fail.back": "Retour",

  // ─── Web app: 404 ──────────────────────────────────────────────────────
  "web.404.kicker": "404",
  "web.404.title": "Cette page n'existe pas.",
  "web.404.body": "Le lien est peut-être obsolète. Retournez à l'accueil.",
  "web.404.cta": "Accueil",

  // ─── Web app: scans list / home ────────────────────────────────────────
  "web.scans.title": "Vos scans",
  "web.scans.subtitle": "Chaque scan surveille un modèle précis et vous notifie dès qu'une affaire apparaît.",
  "web.scans.empty.title": "Aucun scan pour le moment.",
  "web.scans.empty.body": "Lancez votre premier scan pour qu'on commence à surveiller le marché pour vous.",
  "web.scans.newCta": "Nouveau scan",
  "web.scans.col.name": "Nom",
  "web.scans.col.status": "Statut",
  "web.scans.col.alerts": "Alertes 7j",
  "web.scans.col.last": "Dernière alerte",
  "web.scans.status.active": "actif",
  "web.scans.status.paused": "en pause",
  "web.scans.notLogged.title": "Connectez-vous pour voir vos scans",
  "web.scans.notLogged.body": "Un compte gratuit suffit pour commencer.",

  // ─── Web app: create / edit scan ───────────────────────────────────────
  "web.scanNew.title": "Nouveau scan",
  "web.scanNew.subtitle": "Décrivez la voiture que vous cherchez. On scanne 12 sites européens toutes les heures.",
  "web.scanNew.name.label": "Nom du scan",
  "web.scanNew.name.placeholder": "Ma Ferrari 458 idéale",
  "web.scanNew.target.label": "Cible",
  "web.scanNew.target.help": "Marque + modèle. On gère les versions automatiquement.",
  "web.scanNew.year.label": "Année (de — à)",
  "web.scanNew.price.label": "Prix max (€)",
  "web.scanNew.km.label": "Km max",
  "web.scanNew.countries.label": "Pays",
  "web.scanNew.notify.label": "Comment vous prévenir",
  "web.scanNew.notify.email": "Email",
  "web.scanNew.notify.telegram": "Telegram",
  "web.scanNew.submit": "Lancer le scan",
  "web.scanNew.submitting": "Création…",
  "web.scanNew.h1": "Quelle voiture cherchez-vous ?",
  "web.scanNew.subtitleFull":
    "Une seule voiture par scan. On scanne tout le marché européen au pas horaire et on vous alerte sur les Super Price et Good Deals uniquement.",
  "web.scanNew.brand.label": "Marque",
  "web.scanNew.brand.hint": "Ferrari, Audi, Porsche…",
  "web.scanNew.brand.placeholder": "Tapez pour chercher…",
  "web.scanNew.brand.empty": "Aucune marque trouvée — vérifiez l'orthographe",
  "web.scanNew.model.label": "Modèle",
  "web.scanNew.model.hint": "458, RS6, 911 GT3…",
  "web.scanNew.model.placeholder": "Tapez pour chercher…",
  "web.scanNew.model.empty": "Aucun modèle trouvé pour cette marque",
  "web.scanNew.model.disabled": "Choisissez d'abord une marque",
  "web.scanNew.year.label2": "Année visée",
  "web.scanNew.year.hint": "exacte, on élargit ±1 si besoin",
  "web.scanNew.year.from": "À partir de",
  "web.scanNew.year.to": "Jusqu'à",
  "web.scanNew.year.any": "Toutes années",
  "web.scanNew.budget.label": "Budget max",
  "web.scanNew.budget.hint": "€",
  "web.scanNew.km.label2": "Kilométrage max",
  "web.scanNew.km.hint": "km",
  "web.scanNew.countries.hint": "vous pouvez en sélectionner plusieurs",
  "web.scanNew.estimate.kicker": "Estimation marché",
  "web.scanNew.estimate.computing": "Calcul en cours…",
  "web.scanNew.estimate.noData":
    "Pas assez de données pour estimer ce modèle/année. Le scan continuera et alimentera l'estimation.",
  "web.scanNew.estimate.obs": "{n} obs · confiance {confidence}",
  "web.scanNew.cta.helper": "Vous pourrez l'arrêter ou le mettre en pause à tout moment.",
  "country.full.fr": "France",
  "country.full.be": "Belgique",
  "country.full.de": "Allemagne",
  "country.full.it": "Italie",
  "country.full.es": "Espagne",
  "country.full.nl": "Pays-Bas",
  "country.full.ch": "Suisse",
  "country.full.at": "Autriche",
  "country.full.lu": "Luxembourg",

  // ─── Web app: scan dashboard ───────────────────────────────────────────
  "web.scan.tabs.list": "Liste",
  "web.scan.tabs.map": "Carte",
  "web.scan.tabs.scatter": "Prix × Km",
  "web.scan.tabs.chart": "Évolution",
  "web.scan.pause": "Mettre en pause",
  "web.scan.resume": "Relancer",
  "web.scan.delete": "Supprimer",
  "web.scan.empty.title": "Pas encore d'annonces correspondantes.",
  "web.scan.empty.body": "Le scan tourne en continu. On vous notifiera dès qu'une voiture matche.",
  "web.scan.status.pending": "Préparation du scan…",
  "web.scan.status.scraping": "Scan en cours… {done}/{total} sources, {found} annonces trouvées",
  "web.scan.status.error": "Le scan a rencontré une erreur.",
  "web.scan.status.retry": "Réessayer",
  "web.scan.done.zero.title": "Aucune annonce trouvée",
  "web.scan.done.zero.body": "Le scan a parcouru {total} sources mais n'a trouvé aucune annonce correspondant à vos critères.",
  "web.scan.done.zero.hint": "Essayez d'élargir les années, d'ajouter des pays, ou de retirer certains filtres.",
  "web.scan.kicker": "Scan",
  "web.scan.loading": "Chargement…",
  "web.scan.country.all": "Tous pays",
  "web.scan.kpi.active": "Annonces actives",
  "web.scan.kpi.active.hint": "dans le marché ciblé",
  "web.scan.kpi.median": "Prix médian",
  "web.scan.kpi.medianHint": "{n} obs · 30 j",
  "web.scan.kpi.super": "Super Price",
  "web.scan.kpi.good": "Good Deal",
  "web.scan.kpi.over": "Au-dessus du marché",
  "web.scan.results": "{n} résultats",
  "web.scan.filters": "Filtres",
  "web.scan.filter.yearFrom": "Année min",
  "web.scan.filter.yearTo": "Année max",
  "web.scan.filter.priceMax": "Prix max",
  "web.scan.filter.kmMax": "Km max",
  "web.scan.filter.clear": "Effacer",
  "web.scan.currency.label": "Devise",
  "web.scan.view.list": "Liste",
  "web.scan.view.map": "Carte",
  "web.scan.view.scatter": "Scatter",

  // ─── Web app: chart / scatter / map ─────────────────────────────────────
  "web.chart.kicker": "Marché · 90 j",
  "web.chart.trend30d": "{dir} {pct}% · 30 j",
  "web.chart.empty": "Données marché en cours de calcul.",
  "web.scatter.kicker": "Prix × Kilométrage",
  "web.scatter.empty": "Pas de données kilométrage pour ce scan.",
  "web.scatter.compareHint": "Survolez un point pour la photo · cliquez pour ouvrir l'annonce",
  "web.compare.trayLabel": "Comparaison",
  "web.compare.panelTitle": "Comparer les annonces",
  "web.compare.panelHint": "Jusqu'à 4 annonces côte à côte",
  "web.compare.showPanel": "Comparer",
  "web.compare.hidePanel": "Fermer",
  "web.compare.clear": "Tout effacer",
  "web.compare.remove": "Retirer",
  "web.compare.add": "Comparer",
  "web.compare.inList": "Sélectionnée",
  "web.compare.addToList": "Ajouter à la comparaison",
  "web.compare.removeFromList": "Retirer de la comparaison",
  "web.compare.clickToAdd": "Cliquer pour comparer",
  "web.compare.clickToRemove": "Cliquer pour retirer",
  "web.compare.openListing": "Fiche complète",
  "web.compare.viewSource": "Voir sur le site",
  "web.compare.resize": "Redimensionner le panneau de comparaison",

  // Decision signals (pros / cons / risks) — surfaced on cards and detail page
  "web.signals.title": "Pour & contre",
  "web.signals.pros": "Pour",
  "web.signals.cons": "Contre",
  "web.signals.risks": "À vérifier",
  "web.signal.superDeal": "Super affaire",
  "web.signal.goodDeal": "Bon prix",
  "web.signal.belowMarket": "{amount} sous le marché",
  "web.signal.percentBelow": "−{pct}% vs marché",
  "web.signal.percentAbove": "+{pct}% vs marché",
  "web.signal.aboveMarket": "Au-dessus du marché",
  "web.signal.priceDropped": "Baisse de prix récente",
  "web.signal.freshListing": "Annonce fraîche",
  "web.signal.staleListing": "Annonce ancienne",
  "web.signal.daysOn": "En ligne depuis {days} j",
  "web.signal.sellerPro": "Vendeur professionnel",
  "web.signal.sellerPrivate": "Particulier",
  "web.signal.lowKm": "Faible kilométrage",
  "web.signal.highKm": "Kilométrage élevé",
  "web.signal.kmPerYear": "{km} km/an",
  "web.signal.multiSource": "Vue sur {count} sites",
  "web.signal.noPhotos": "Aucune photo",
  "web.signal.fewPhotos": "Peu de photos ({count})",
  "web.signal.savingsSuspect": "Économie à confirmer",
  "web.signal.savingsSuspectDetail": "L'écart au marché paraît trop large pour être fiable",
  "web.signal.lowConfidence": "Estimation peu fiable",
  "web.signal.lowConfidenceDetail": "Peu d'annonces comparables — la note est indicative",
  "web.listing.daysOnline": "En ligne depuis",
  "web.listing.daysValue": "{days} jours",
  "web.listing.location": "Emplacement",
  "web.listing.seller": "Vendeur",
  "web.listing.priceHistoryEmpty": "Pas encore d'historique de prix.",
  "web.map.kicker": "Carte",
  "web.map.count": "{n} annonces actives, géolocalisation en cours d'indexation.",
  "web.map.placeholder": "La vue carte sera disponible prochainement avec les emplacements géographiques des annonces.",

  // ─── Web app: listing detail ───────────────────────────────────────────
  "web.listing.back": "← Toutes les annonces",
  "web.listing.source": "Source",
  "web.listing.seenOn": "Vu sur",
  "web.listing.firstSeen": "Première observation",
  "web.listing.lastSeen": "Dernière observation",
  "web.listing.fairPrice": "Prix juste",
  "web.listing.priceRange": "Fourchette p10–p90",
  "web.listing.openSource": "Ouvrir l'annonce source",
  "web.listing.priceHistory": "Historique de prix",
  "web.listing.untitled": "Annonce sans titre",
  "web.listing.notFound": "Annonce introuvable.",
  "web.listing.seenPrefix": "· Vue {when}",
  "web.listing.fairEstimated": "Prix juste estimé",
  "web.listing.p10": "Fourchette p10",
  "web.listing.p90": "Fourchette p90",
  "web.listing.savingsEstimated": "Économie estimée",
  "web.listing.dealScore": "Score de deal",
  "web.listing.confidence": "Confiance",
  "web.listing.history": "Historique",
  "web.listing.photosPending": "Photos en cours d'archivage.",
  "web.listing.specs": "Caractéristiques",
  "web.listing.specs.doors": "Portes",
  "web.listing.specs.seats": "Places",
  "web.listing.specs.engineCc": "Cylindrée",
  "web.listing.specs.driveType": "Transmission",
  "web.listing.specs.co2": "CO₂",
  "web.listing.specs.firstRegistration": "1ère immatriculation",
  "web.listing.specs.fuel": "Carburant",
  "web.listing.specs.gearbox": "Boîte",
  "web.listing.specs.hp": "Puissance",
  "web.listing.specs.body": "Carrosserie",
  "web.listing.specs.color": "Couleur",
  "web.listing.description": "Description",
  "web.listing.marketData": "Données de marché",

  // ─── Web app: account ──────────────────────────────────────────────────
  "web.account.title": "Votre compte",
  "web.account.email": "Email",
  "web.account.plan": "Plan",
  "web.account.language": "Langue",
  "web.account.languageHelp": "On vous écrit dans cette langue (emails, notifications).",
  "web.account.signout": "Déconnexion",
  "web.account.dangerZone": "Zone de danger",
  "web.account.delete": "Supprimer mon compte",
  "web.account.kicker": "Compte",
  "web.account.h1": "Réglages",
  "web.account.section.subscription": "Abonnement",
  "web.account.section.notifications": "Notifications",
  "web.account.section.data": "Données",
  "web.account.subscription.body":
    "Tarification hebdomadaire. Pause à tout moment, aucun frais quand le scan est en pause. Le provider de paiement sera branché dans une seconde phase (scaffold seulement pour l'instant).",
  "web.account.notif.push": "Push navigateur (par défaut)",
  "web.account.notif.email": "Email (par défaut)",
  "web.account.notif.telegram": "Telegram (Hunter Pro et au-dessus)",
  "web.account.notif.slack": "Slack / Webhook (Agency)",
  "web.account.data.body":
    "Vos scans, vos annonces sauvegardées et votre historique restent les vôtres. Export CSV disponible à tout moment depuis chaque scan (Hunter Pro+).",
  "web.account.bullet.free": "1 scan actif",
  "web.account.bullet.hunter": "3 scans + push",
  "web.account.bullet.pro": "10 scans + Telegram + rapports",
  "web.account.bullet.agency": "Illimité + Slack + multi-user",

  // ─── Web app: market browse ────────────────────────────────────────────
  "web.market.title": "Le marché en temps réel",
  "web.market.subtitle": "Parcourez les modèles les plus actifs en ce moment. Cliquez pour voir les annonces.",
  "web.market.empty": "Aucun modèle indexé pour le moment.",
  "web.market.modelSubtitle": "Annonces actives classées par score d'affaire. Cliquez sur une carte pour le détail.",
  "web.market.modelEmpty": "Aucune annonce active pour ce modèle pour le moment.",
  "web.market.createHunt": "Lancer une veille",
  "web.market.listingsCount": "{count} annonces affichées",
  "web.list.col.deal": "Deal",
  "web.list.col.country": "Pays",
  "web.list.col.title": "Titre",
  "web.list.col.year": "Année",
  "web.list.col.km": "Kilométrage",
  "web.list.col.price": "Prix",
  "web.list.col.fairPrice": "Prix juste",
  "web.list.col.savings": "Économie",
  "web.list.col.seen": "Vu",
  "web.list.empty": "Aucune affaire au-dessus du seuil pour le moment. On rescanne dans 30 min.",

  // ─── SEO surface ───────────────────────────────────────────────────────
  "seo.home.title": "MotorScan · Le scanner européen d'annonces de voitures",
  "seo.home.description":
    "Choisissez votre voiture, on surveille tout le marché européen 24/7 et on vous notifie au moment où une vraie affaire apparaît.",
  "seo.home.kicker": "Marché · {modelsCount} fiches modèles · {indexedCount} pages indexées",
  "seo.home.hero": "L'observatoire en temps réel du marché auto européen.",
  "seo.home.body":
    "Pour chaque modèle, on agrège les annonces de 12 sites européens et on calcule le prix juste, les fourchettes p10–p90, et la profondeur du marché. Mis à jour toutes les heures.",
  "seo.home.activeModels": "Modèles les plus actifs",
  "seo.home.topToday": "Le top du marché aujourd'hui",
  "seo.home.empty":
    "Les fiches modèles seront affichées dès que la base aura accumulé suffisamment d'annonces actives.",

  "seo.breadcrumb.market": "Marché",
  "seo.kicker.brand": "Marque",
  "seo.kicker.brandSpace": "Marché · {brand}",
  "seo.kicker.market_year": "Marché · {brand} {model} · millésime {year}",
  "seo.kicker.market_country": "Marché · {brand} {model} · {country}",
  "seo.kicker.year": "Année {year}",
  "seo.kicker.country": "Pays",

  "seo.brand.title": "{brand} d'occasion · {modelsCount} modèles, {activeCount} annonces",
  "seo.brand.description":
    "Le marché européen {brand} d'occasion : {modelsCount} modèles avec {activeCount} annonces actives, mises à jour toutes les heures.",
  "seo.brand.h1": "{brand} d'occasion en Europe",
  "seo.brand.body":
    "{modelsCount} modèles surveillés. {activeCount} annonces actives au moment de la dernière mise à jour.",
  "seo.brand.models": "Modèles",
  "seo.brand.adsSuffix": "annonces",

  "seo.model.title": "{brand} {model} d'occasion · prix juste, {n} annonces en Europe",
  "seo.model.description":
    "Marché en temps réel de la {brand} {model}. {n} annonces actives en Europe, prix médian {median}, {deals} bonnes affaires identifiées sur les 30 derniers jours.",
  "seo.model.h1": "{brand} {model} d'occasion",
  "seo.model.body":
    "{n} annonces actives en Europe sur les 30 derniers jours. Prix médian observé : {median}.",
  "seo.model.bodyDealsExtra": " {deals} affaires sous le prix juste actuellement.",
  "seo.model.kpi.active": "Annonces actives",
  "seo.model.kpi.median": "Prix médian",
  "seo.model.kpi.deals": "Bonnes affaires",
  "seo.model.kpi.last": "Données 30j",
  "seo.model.deals.kicker": "Top affaires actuelles",
  "seo.model.deals.title": "Annonces actives triées par économie",
  "seo.model.deals.cta": "Voir les {n} annonces · gratuit",
  "seo.model.deals.cta.helper":
    "5 visibles ici. Connectez-vous pour les voir toutes et être notifié des nouvelles.",
  "seo.model.deals.empty": "Pas d'annonces actives pour ce modèle pour le moment.",
  "seo.model.market.kicker": "Répartition Deal Rating · 30 j",
  "seo.model.market.title": "Le marché en un coup d'œil",
  "seo.model.watch.title": "Surveillez cette voiture en continu",
  "seo.model.watch.body":
    "Cette page est mise à jour toutes les heures. Mais vous, vous ne voulez pas la rafraîchir 5 fois par jour. Lancez un scan MotorScan : on regarde le marché pour vous et on vous notifie au moment où une vraie affaire apparaît.",
  "seo.model.faq.kicker": "Questions fréquentes",
  "seo.model.faq.title": "Tout savoir avant d'acheter une {brand} {model}",
  "seo.model.faq.q1": "Quel est le prix d'une {brand} {model} d'occasion ?",
  "seo.model.faq.a1":
    "Sur les 30 derniers jours, le prix médian observé d'une {brand} {model} d'occasion en Europe est {median}, calculé sur {n} annonces actives. Les fourchettes p10–p90 sont visibles dans la fiche de chaque annonce.",
  "seo.model.faq.q2": "Comment savoir si une {brand} {model} est une bonne affaire ?",
  "seo.model.faq.a2":
    "MotorScan calcule un Deal Rating pour chaque annonce : on compare le prix demandé au prix juste estimé à partir des annonces comparables (même version, même année, même kilométrage, même pays). Une affaire est classée \"Super Price\" quand l'économie est ≥ 10 % vs ce prix juste.",
  "seo.model.faq.q3": "Sur quels sites MotorScan trouve-t-il ses annonces ?",
  "seo.model.faq.a3":
    "LeBonCoin, La Centrale, AutoScout24, Mobile.de, AutoScout24.it, Coches.net, 2ememain, ainsi que les principaux sites suisses, autrichiens et néerlandais. La liste s'élargit en continu, et chaque source est rafraîchie au moins toutes les heures.",
  "seo.model.faq.q4": "Combien coûte MotorScan ?",
  "seo.model.faq.a4":
    "Consulter le marché est gratuit. Pour surveiller un modèle en continu et recevoir une notification dès qu'une vraie affaire apparaît, l'offre Hunter est à 9,90 €/semaine, sans engagement.",

  "seo.year.title": "{brand} {model} {year} d'occasion · prix juste · {n} annonces",
  "seo.year.description":
    "Prix juste, p10/p90, et top affaires actuelles pour la {brand} {model} de {year}. {n} annonces actives en Europe.",
  "seo.year.h1": "{brand} {model} {year} d'occasion",
  "seo.year.body": "{n} annonces actives pour ce millésime. Prix médian observé : {median}.",
  "seo.year.deals.title": "Top affaires {year}",
  "seo.year.deals.empty":
    "Pas d'annonces pour ce millésime au moment de la dernière mise à jour.",
  "seo.year.cta": "Surveiller en continu · gratuit",

  "seo.country.title": "{brand} {model} d'occasion en {country} · {n} annonces",
  "seo.country.description":
    "{n} {brand} {model} en vente en {country}, prix médian {median}. Top affaires mises à jour toutes les heures.",
  "seo.country.h1": "{brand} {model} d'occasion en {country}",
  "seo.country.body": "{n} annonces actives. Prix médian observé : {median}.",
  "seo.country.deals.title": "Top affaires · {country}",
  "seo.country.deals.empty": "Pas d'annonces dans ce pays au moment de la dernière mise à jour.",

  "seo.deals.col.deal": "Deal",
  "seo.deals.col.country": "Pays",
  "seo.deals.col.listing": "Annonce",
  "seo.deals.col.year": "Année",
  "seo.deals.col.km": "Km",
  "seo.deals.col.price": "Prix",
  "seo.deals.col.savings": "Économie",
  "seo.deals.signinHint": "Connectez-vous",

  // 404
  "seo.404.title": "Page introuvable · MotorScan",
  "seo.404.description": "Cette fiche n'existe plus ou n'a pas (encore) été indexée.",
  "seo.404.kicker": "404",
  "seo.404.h1": "Pas de fiche ici.",
  "seo.404.body":
    "Le modèle ou millésime n'a pas (encore) accumulé assez d'annonces pour avoir sa propre page. Lancez un scan pour le surveiller en continu.",

  // Country names (used in /pays/[country] pages)
  "country.fr": "France",
  "country.be": "Belgique",
  "country.ch": "Suisse",
  "country.de": "Allemagne",
  "country.it": "Italie",
  "country.es": "Espagne",
  "country.at": "Autriche",
  "country.lu": "Luxembourg",
  "country.nl": "Pays-Bas",

  // ─── OG image labels ───────────────────────────────────────────────────
  "og.kicker.market": "Marché",
  "og.kicker.country": "Pays",
  "og.kicker.year": "Année {year}",
  "og.kicker.brand": "MotorScan",
  "og.stat.listings": "Annonces",
  "og.stat.median": "Médian",
  "og.stat.deals": "Bonnes affaires",
  "og.stat.sources": "Sources",
  "og.stat.countries": "Pays",
  "og.stat.freshness": "Fraîcheur",
  "og.home.title": "L'observatoire en temps réel du marché auto européen.",
  "og.home.subtitle": "12 sites européens agrégés. Mis à jour toutes les heures.",
  "og.model.subtitle":
    "{n} annonces actives en Europe. Prix médian {median}.",
} as const;

export type DictKey = keyof typeof dictFr;

/**
 * English dictionary. Same keys as `dictFr` (enforced via `Record<DictKey, string>`).
 *
 * Editorial principles match the FR side: precise, restrained, no marketing
 * fluff, no em-dashes (use commas / colons / parens). When unsure between
 * "European market" and "EU market", prefer "European" — Switzerland and UK
 * count.
 */
export const dictEn: Record<DictKey, string> = {
  "common.loading": "Loading…",
  "common.error": "Something went wrong.",
  "common.retry": "Retry",
  "common.cancel": "Cancel",
  "common.save": "Save",
  "common.close": "Close",
  "common.back": "Back",
  "common.next": "Next",
  "common.signin": "Sign in",
  "common.signout": "Sign out",
  "common.signup_free": "Try for free",
  "common.startScan": "Start a scan",
  "common.startScanFree": "Start a free scan",
  "common.viewListing": "View listing",
  "common.view": "View →",
  "common.connecting": "Signing in…",
  "common.sending": "Sending…",

  "nav.scans": "My scans",
  "nav.newScan": "New scan",
  "nav.market": "Market",
  "nav.account": "Account",
  "footer.tagline": "The European car-listings scanner. One car, watched 24/7.",
  "footer.product": "Product",
  "footer.market": "Market",
  "footer.legal": "Legal",
  "footer.legalMentions": "Legal notice",
  "footer.cgu": "Terms",
  "footer.app": "The app",
  "footer.copyright": "© {year} MotorScan · Data aggregated from public listings sites · Not affiliated with any brand mentioned.",

  "locale.fr": "Français",
  "locale.en": "English",

  "landing.kicker": "MotorScan · car-listings scanner · 12 sites · 5 countries",
  "landing.hero.title": "We scan the European market for the car you want.",
  "landing.hero.subtitle":
    "Describe the car, the budget, the geography. Every hour we check LeBonCoin, La Centrale, Mobile.de, AutoScout24, and 8 other sites. We ping you when a real deal shows up, not a promo, not a marketing stunt, just a price actually below market.",
  "landing.hero.exploreMarket": "Explore the market",
  "landing.demo.kicker": "Live demo · Ferrari 458 scan · real data",
  "landing.demo.deals": "{n} deals",
  "landing.demo.col.deal": "Deal",
  "landing.demo.col.listing": "Listing",
  "landing.demo.col.year": "Year",
  "landing.demo.col.km": "Km",
  "landing.demo.col.price": "Price",
  "landing.demo.col.savings": "Savings",
  "landing.demo.col.seen": "Seen",
  "landing.how.kicker": "How it works",
  "landing.how.title": "Three steps. Then wait for the ping.",
  "landing.how.step1.h": "You define the target",
  "landing.how.step1.p":
    "Make, model, year, budget, mileage, countries to scan. Precise like a La Centrale search, except you set it once.",
  "landing.how.step2.h": "We scan continuously",
  "landing.how.step2.p":
    "Every hour, 12 European sites. LeBonCoin, La Centrale, AutoScout24, Mobile.de, Subito, AutoTrader, Coches.net, and five more. No site escapes the scanner.",
  "landing.how.step3.h": "We ping you at the right moment",
  "landing.how.step3.p":
    "Push, email, or Telegram. Only when a listing crosses your Deal Rating threshold. No spam, no courtesy notifications.",
  "landing.fairprice.kicker": "Fair Price",
  "landing.fairprice.title": "A fair price, computed from 30 days of real observations.",
  "landing.fairprice.body1":
    "We aggregate every listing seen in the past 30 days by (make, model, version, country, year) and compute the median, p10 and p90 percentiles, and the mileage effect. The result: a fair price per car, refreshed every 30 minutes.",
  "landing.fairprice.body2":
    "When the sample is too thin (fewer than 5 listings on the exact target), we widen to year ± 1, then to all countries, and we openly show the confidence level. No invented values.",
  "landing.fairprice.levels": "The 5 levels",
  "landing.fairprice.super": "≤ p10 · below the 10th percentile of the market",
  "landing.fairprice.good": "> p10, ≤ median · a reasonable good deal",
  "landing.fairprice.fair": "around the fair price",
  "landing.fairprice.high": "above market",
  "landing.fairprice.over": "> p90 · clearly overpriced",
  "landing.pricing.kicker": "Pricing",
  "landing.pricing.title": "Weekly. Pause anytime.",
  "landing.pricing.intro":
    "You'll buy your car in 2 or 3 weeks. Why pay for a full month? Pause is one click. Cancel when you've found it.",
  "landing.pricing.unitWeek": "€ / wk",
  "landing.pricing.unitMonth": "€ / mo",
  "landing.pricing.free.tagline": "Get a feel for it. 1 active scan, daily email alerts.",
  "landing.pricing.hunter.tagline": "3 scans, push + email, Super Price filter.",
  "landing.pricing.hunterPro.tagline": "10 scans, Telegram, weekly reports, comparator.",
  "landing.pricing.agency.tagline": "Unlimited, Slack/webhook, CSV, multi-user.",
  "landing.pricing.free.cta": "Get started",
  "landing.pricing.hunter.cta": "Pick Hunter",
  "landing.pricing.hunterPro.cta": "Pick Pro",
  "landing.pricing.agency.cta": "Contact us",
  "landing.pricing.disclaimer":
    "The payment method will be announced shortly. Free accounts are open right now.",
  "landing.faq.kicker": "FAQ",
  "landing.faq.title": "Questions we hear most often",
  "landing.faq.q1": "Are you a car dealer?",
  "landing.faq.a1":
    "No. MotorScan sells nothing. We point you to source listings (LeBonCoin, La Centrale, etc.). We never touch the transaction.",
  "landing.faq.q2": "Which sites do you scan?",
  "landing.faq.a2":
    "Today: LeBonCoin, La Centrale, AutoScout24 (FR/DE), Mobile.de, Subito, Comparis, Coches.net, Tweedehands, LeParking, AutoTrader, plus a few aggregators. Every hour.",
  "landing.faq.q3": "Do you really keep 30 days of history?",
  "landing.faq.a3":
    "Yes, and more. We store every observation (listing + price + km + status) in a time-series store. That's what powers the evolution charts and the Fair Price.",
  "landing.faq.q5": "Is it legal to scan these sites?",
  "landing.faq.a5":
    "We don't scrape listings to republish them. We notify the end user, who clicks through to the source listing. Same usage pattern as a human refreshing their tab.",

  "auth.signin.title": "Sign in by email",
  "auth.signin.subtitle":
    "No password. We send you a 6-digit code by email.",
  "auth.signin.emailLabel": "Email",
  "auth.signin.cta": "Send the code",
  "auth.signin.sentTitle": "Enter your code.",
  "auth.signin.sentBody":
    "A 6-digit code was sent to {email}.",
  "auth.signin.otpLabel": "6-digit code",
  "auth.signin.otpCta": "Sign in",
  "auth.signin.otpError": "Invalid or expired code. Try again.",
  "auth.signin.resend": "Resend code",
  "auth.signin.error": "Couldn't send. Double-check the email and try again.",
  "auth.signin.placeholder": "you@example.com",
  "auth.email.subject": "Your MotorScan code: {code}",
  "auth.email.intro": "Welcome to MotorScan.",
  "auth.email.body": "Here's your sign-in code, valid for {minutes} minutes:",
  "auth.email.cta": "Sign in",
  "auth.email.fallbackLabel": "Or click the button below:",
  "auth.email.footer": "If you didn't request this, just ignore the email.",
  "auth.email.textIntro": "Welcome.",
  "auth.email.textOtp": "Your sign-in code: {code} (valid for {minutes} min)",
  "auth.email.textFallback": "Or click this link:",
  "auth.email.textBody": "Click here to sign in (link valid for {minutes} min):",
  "auth.email.textFooter": "If you didn't request this, just ignore this email.",
  "auth.fail.kicker": "Sign-in",
  "auth.fail.title": "No can do.",
  "auth.fail.message": "Invalid or expired link. Request a new one.",
  "auth.fail.back": "Back",

  "web.404.kicker": "404",
  "web.404.title": "This page doesn't exist.",
  "web.404.body": "The link is probably stale. Head back home.",
  "web.404.cta": "Home",

  "web.scans.title": "Your scans",
  "web.scans.subtitle": "Each scan watches a specific car and pings you the moment a deal shows up.",
  "web.scans.empty.title": "No scans yet.",
  "web.scans.empty.body": "Start your first scan and we'll begin watching the market for you.",
  "web.scans.newCta": "New scan",
  "web.scans.col.name": "Name",
  "web.scans.col.status": "Status",
  "web.scans.col.alerts": "Alerts (7d)",
  "web.scans.col.last": "Last alert",
  "web.scans.status.active": "active",
  "web.scans.status.paused": "paused",
  "web.scans.notLogged.title": "Sign in to see your scans",
  "web.scans.notLogged.body": "A free account is all you need to get going.",

  "web.scanNew.title": "New scan",
  "web.scanNew.subtitle": "Describe the car you're after. We scan 12 European sites every hour.",
  "web.scanNew.name.label": "Scan name",
  "web.scanNew.name.placeholder": "My ideal Ferrari 458",
  "web.scanNew.target.label": "Target",
  "web.scanNew.target.help": "Make + model. We handle versions automatically.",
  "web.scanNew.year.label": "Year (from — to)",
  "web.scanNew.price.label": "Max price (€)",
  "web.scanNew.km.label": "Max km",
  "web.scanNew.countries.label": "Countries",
  "web.scanNew.notify.label": "How to ping you",
  "web.scanNew.notify.email": "Email",
  "web.scanNew.notify.telegram": "Telegram",
  "web.scanNew.submit": "Start scan",
  "web.scanNew.submitting": "Creating…",
  "web.scanNew.h1": "Which car are you after?",
  "web.scanNew.subtitleFull":
    "One car per scan. We watch the whole European market hourly and only ping you for Super Price and Good deals.",
  "web.scanNew.brand.label": "Make",
  "web.scanNew.brand.hint": "Ferrari, Audi, Porsche…",
  "web.scanNew.brand.placeholder": "Type to search…",
  "web.scanNew.brand.empty": "No make found — check the spelling",
  "web.scanNew.model.label": "Model",
  "web.scanNew.model.hint": "458, RS6, 911 GT3…",
  "web.scanNew.model.placeholder": "Type to search…",
  "web.scanNew.model.empty": "No model found for this make",
  "web.scanNew.model.disabled": "Pick a make first",
  "web.scanNew.year.label2": "Target year",
  "web.scanNew.year.hint": "exact, we widen ±1 if needed",
  "web.scanNew.year.from": "From",
  "web.scanNew.year.to": "Until",
  "web.scanNew.year.any": "Any year",
  "web.scanNew.budget.label": "Max budget",
  "web.scanNew.budget.hint": "€",
  "web.scanNew.km.label2": "Max mileage",
  "web.scanNew.km.hint": "km",
  "web.scanNew.countries.hint": "select as many as you like",
  "web.scanNew.estimate.kicker": "Market estimate",
  "web.scanNew.estimate.computing": "Computing…",
  "web.scanNew.estimate.noData":
    "Not enough data to estimate this model/year yet. The scan will keep running and feed the estimate.",
  "web.scanNew.estimate.obs": "{n} obs · confidence {confidence}",
  "web.scanNew.cta.helper": "You can pause or stop it at any time.",
  "country.full.fr": "France",
  "country.full.be": "Belgium",
  "country.full.de": "Germany",
  "country.full.it": "Italy",
  "country.full.es": "Spain",
  "country.full.nl": "Netherlands",
  "country.full.ch": "Switzerland",
  "country.full.at": "Austria",
  "country.full.lu": "Luxembourg",

  "web.scan.tabs.list": "List",
  "web.scan.tabs.map": "Map",
  "web.scan.tabs.scatter": "Price × Km",
  "web.scan.tabs.chart": "Trend",
  "web.scan.pause": "Pause",
  "web.scan.resume": "Resume",
  "web.scan.delete": "Delete",
  "web.scan.empty.title": "No matching listings yet.",
  "web.scan.empty.body": "The scan keeps running. We'll ping you the moment a car matches.",
  "web.scan.status.pending": "Preparing scan…",
  "web.scan.status.scraping": "Scanning… {done}/{total} sources, {found} listings found",
  "web.scan.status.error": "The scan encountered an error.",
  "web.scan.status.retry": "Retry",
  "web.scan.done.zero.title": "No listings found",
  "web.scan.done.zero.body": "The scan searched {total} sources but found no listings matching your criteria.",
  "web.scan.done.zero.hint": "Try widening the year range, adding countries, or removing some filters.",
  "web.scan.kicker": "Scan",
  "web.scan.loading": "Loading…",
  "web.scan.country.all": "All countries",
  "web.scan.kpi.active": "Active listings",
  "web.scan.kpi.active.hint": "in the target market",
  "web.scan.kpi.median": "Median price",
  "web.scan.kpi.medianHint": "{n} obs · 30 d",
  "web.scan.kpi.super": "Super Price",
  "web.scan.kpi.good": "Good Deal",
  "web.scan.kpi.over": "Above market",
  "web.scan.results": "{n} results",
  "web.scan.filters": "Filters",
  "web.scan.filter.yearFrom": "Year from",
  "web.scan.filter.yearTo": "Year to",
  "web.scan.filter.priceMax": "Max price",
  "web.scan.filter.kmMax": "Max km",
  "web.scan.filter.clear": "Clear",
  "web.scan.currency.label": "Currency",
  "web.scan.view.list": "List",
  "web.scan.view.map": "Map",
  "web.scan.view.scatter": "Scatter",

  "web.chart.kicker": "Market · 90 d",
  "web.chart.trend30d": "{dir} {pct}% · 30 d",
  "web.chart.empty": "Market data being computed.",
  "web.scatter.kicker": "Price × Mileage",
  "web.scatter.empty": "No mileage data for this scan.",
  "web.scatter.compareHint": "Hover a dot for the photo · click to open the listing",
  "web.compare.trayLabel": "Compare",
  "web.compare.panelTitle": "Compare listings",
  "web.compare.panelHint": "Up to 4 listings side by side",
  "web.compare.showPanel": "Compare",
  "web.compare.hidePanel": "Close",
  "web.compare.clear": "Clear all",
  "web.compare.remove": "Remove",
  "web.compare.add": "Compare",
  "web.compare.inList": "Selected",
  "web.compare.addToList": "Add to comparison",
  "web.compare.removeFromList": "Remove from comparison",
  "web.compare.clickToAdd": "Click to compare",
  "web.compare.clickToRemove": "Click to remove",
  "web.compare.openListing": "Full listing",
  "web.compare.viewSource": "View on site",
  "web.compare.resize": "Resize comparison panel",

  // Decision signals (pros / cons / risks)
  "web.signals.title": "Pros & cons",
  "web.signals.pros": "Pros",
  "web.signals.cons": "Cons",
  "web.signals.risks": "Watch out",
  "web.signal.superDeal": "Super deal",
  "web.signal.goodDeal": "Good price",
  "web.signal.belowMarket": "{amount} below market",
  "web.signal.percentBelow": "−{pct}% vs market",
  "web.signal.percentAbove": "+{pct}% vs market",
  "web.signal.aboveMarket": "Above market",
  "web.signal.priceDropped": "Recent price drop",
  "web.signal.freshListing": "Fresh listing",
  "web.signal.staleListing": "Stale listing",
  "web.signal.daysOn": "Online for {days}d",
  "web.signal.sellerPro": "Dealer",
  "web.signal.sellerPrivate": "Private seller",
  "web.signal.lowKm": "Low mileage",
  "web.signal.highKm": "High mileage",
  "web.signal.kmPerYear": "{km} km/year",
  "web.signal.multiSource": "Listed on {count} sites",
  "web.signal.noPhotos": "No photos",
  "web.signal.fewPhotos": "Few photos ({count})",
  "web.signal.savingsSuspect": "Savings to verify",
  "web.signal.savingsSuspectDetail": "The market gap looks too wide to trust as-is",
  "web.signal.lowConfidence": "Low-confidence estimate",
  "web.signal.lowConfidenceDetail": "Few comparable listings — rating is indicative only",
  "web.listing.daysOnline": "Online for",
  "web.listing.daysValue": "{days} days",
  "web.listing.location": "Location",
  "web.listing.seller": "Seller",
  "web.listing.priceHistoryEmpty": "No price history yet.",
  "web.map.kicker": "Map",
  "web.map.count": "{n} active listings, geolocation being indexed.",
  "web.map.placeholder": "The map view will be available soon with geographic listing locations.",

  "web.listing.back": "← All listings",
  "web.listing.source": "Source",
  "web.listing.seenOn": "Seen on",
  "web.listing.firstSeen": "First seen",
  "web.listing.lastSeen": "Last seen",
  "web.listing.fairPrice": "Fair price",
  "web.listing.priceRange": "p10–p90 band",
  "web.listing.openSource": "Open source listing",
  "web.listing.priceHistory": "Price history",
  "web.listing.untitled": "Untitled listing",
  "web.listing.notFound": "Listing not found.",
  "web.listing.seenPrefix": "· Seen {when}",
  "web.listing.fairEstimated": "Estimated fair price",
  "web.listing.p10": "p10 band",
  "web.listing.p90": "p90 band",
  "web.listing.savingsEstimated": "Estimated savings",
  "web.listing.dealScore": "Deal score",
  "web.listing.confidence": "Confidence",
  "web.listing.history": "History",
  "web.listing.photosPending": "Photos still being archived.",
  "web.listing.specs": "Specifications",
  "web.listing.specs.doors": "Doors",
  "web.listing.specs.seats": "Seats",
  "web.listing.specs.engineCc": "Displacement",
  "web.listing.specs.driveType": "Drivetrain",
  "web.listing.specs.co2": "CO₂",
  "web.listing.specs.firstRegistration": "First registration",
  "web.listing.specs.fuel": "Fuel",
  "web.listing.specs.gearbox": "Gearbox",
  "web.listing.specs.hp": "Power",
  "web.listing.specs.body": "Body",
  "web.listing.specs.color": "Color",
  "web.listing.description": "Description",
  "web.listing.marketData": "Market data",

  "web.account.title": "Your account",
  "web.account.email": "Email",
  "web.account.plan": "Plan",
  "web.account.language": "Language",
  "web.account.languageHelp": "We'll write to you in this language (emails, notifications).",
  "web.account.signout": "Sign out",
  "web.account.dangerZone": "Danger zone",
  "web.account.delete": "Delete my account",
  "web.account.kicker": "Account",
  "web.account.h1": "Settings",
  "web.account.section.subscription": "Subscription",
  "web.account.section.notifications": "Notifications",
  "web.account.section.data": "Data",
  "web.account.subscription.body":
    "Weekly billing. Pause any time, no charge while paused. The payment provider gets wired in in a second phase (scaffold only for now).",
  "web.account.notif.push": "Browser push (default)",
  "web.account.notif.email": "Email (default)",
  "web.account.notif.telegram": "Telegram (Hunter Pro and up)",
  "web.account.notif.slack": "Slack / Webhook (Agency)",
  "web.account.data.body":
    "Your scans, saved listings, and history remain yours. CSV export is available at any time from each scan (Hunter Pro+).",
  "web.account.bullet.free": "1 active scan",
  "web.account.bullet.hunter": "3 scans + push",
  "web.account.bullet.pro": "10 scans + Telegram + reports",
  "web.account.bullet.agency": "Unlimited + Slack + multi-user",

  "web.market.title": "The market in real time",
  "web.market.subtitle": "Browse the most active models right now. Click in to see the listings.",
  "web.market.empty": "No models indexed yet.",
  "web.market.modelSubtitle": "Active listings ranked by deal score. Click a card for details.",
  "web.market.modelEmpty": "No active listings for this model right now.",
  "web.market.createHunt": "Start a hunt",
  "web.market.listingsCount": "{count} listings shown",
  "web.list.col.deal": "Deal",
  "web.list.col.country": "Country",
  "web.list.col.title": "Title",
  "web.list.col.year": "Year",
  "web.list.col.km": "Mileage",
  "web.list.col.price": "Price",
  "web.list.col.fairPrice": "Fair price",
  "web.list.col.savings": "Savings",
  "web.list.col.seen": "Seen",
  "web.list.empty": "No deals above the threshold right now. Re-scanning in 30 min.",

  "seo.home.title": "MotorScan · the European car-listings scanner",
  "seo.home.description":
    "Pick your car, we watch the whole European market 24/7 and we ping you the moment a real deal shows up.",
  "seo.home.kicker": "Market · {modelsCount} model pages · {indexedCount} indexed pages",
  "seo.home.hero": "The real-time observatory of the European used-car market.",
  "seo.home.body":
    "For every model, we aggregate listings from 12 European sites and compute the fair price, p10–p90 bands, and market depth. Refreshed hourly.",
  "seo.home.activeModels": "Most active models",
  "seo.home.topToday": "Today's top of the market",
  "seo.home.empty":
    "Model pages will appear here as soon as the database has accumulated enough active listings.",

  "seo.breadcrumb.market": "Market",
  "seo.kicker.brand": "Brand",
  "seo.kicker.brandSpace": "Market · {brand}",
  "seo.kicker.market_year": "Market · {brand} {model} · {year} model year",
  "seo.kicker.market_country": "Market · {brand} {model} · {country}",
  "seo.kicker.year": "Year {year}",
  "seo.kicker.country": "Country",

  "seo.brand.title": "Used {brand} · {modelsCount} models, {activeCount} listings",
  "seo.brand.description":
    "The European used-{brand} market: {modelsCount} models with {activeCount} active listings, refreshed hourly.",
  "seo.brand.h1": "Used {brand} in Europe",
  "seo.brand.body":
    "{modelsCount} models tracked. {activeCount} active listings as of the last refresh.",
  "seo.brand.models": "Models",
  "seo.brand.adsSuffix": "listings",

  "seo.model.title": "Used {brand} {model} · fair price, {n} listings in Europe",
  "seo.model.description":
    "Real-time market for the {brand} {model}. {n} active listings in Europe, median price {median}, {deals} below-fair-price deals identified in the past 30 days.",
  "seo.model.h1": "Used {brand} {model}",
  "seo.model.body":
    "{n} active listings in Europe over the past 30 days. Observed median price: {median}.",
  "seo.model.bodyDealsExtra": " {deals} deals below the fair price right now.",
  "seo.model.kpi.active": "Active listings",
  "seo.model.kpi.median": "Median price",
  "seo.model.kpi.deals": "Below-fair deals",
  "seo.model.kpi.last": "30-day data",
  "seo.model.deals.kicker": "Best current deals",
  "seo.model.deals.title": "Active listings, sorted by savings",
  "seo.model.deals.cta": "See all {n} listings · free",
  "seo.model.deals.cta.helper":
    "5 visible here. Sign in to see them all and get pinged on new ones.",
  "seo.model.deals.empty": "No active listings for this model right now.",
  "seo.model.market.kicker": "Deal Rating distribution · 30 d",
  "seo.model.market.title": "The market at a glance",
  "seo.model.watch.title": "Watch this car continuously",
  "seo.model.watch.body":
    "This page is refreshed hourly. But you don't want to reload it 5 times a day. Start a MotorScan: we watch the market for you and ping you the moment a real deal appears.",
  "seo.model.faq.kicker": "Frequently asked",
  "seo.model.faq.title": "What to know before buying a {brand} {model}",
  "seo.model.faq.q1": "What does a used {brand} {model} cost?",
  "seo.model.faq.a1":
    "Over the past 30 days, the observed median price of a used {brand} {model} in Europe is {median}, computed across {n} active listings. p10 and p90 bands are visible in each listing's detail page.",
  "seo.model.faq.q2": "How do you know whether a {brand} {model} is a good deal?",
  "seo.model.faq.a2":
    "MotorScan computes a Deal Rating for every listing: we compare the asking price to a fair price estimated from comparable listings (same version, year, mileage band, country). A deal is classified \"Super Price\" when savings are ≥ 10 % vs that fair price.",
  "seo.model.faq.q3": "Which sites does MotorScan pull listings from?",
  "seo.model.faq.a3":
    "LeBonCoin, La Centrale, AutoScout24, Mobile.de, AutoScout24.it, Coches.net, 2ememain, plus the main Swiss, Austrian and Dutch sites. The list keeps growing, and every source is refreshed at least hourly.",
  "seo.model.faq.q4": "How much does MotorScan cost?",
  "seo.model.faq.a4":
    "Browsing the market is free. To watch a model continuously and get pinged the moment a real deal appears, the Hunter plan is 9.90 € / week, no commitment.",

  "seo.year.title": "Used {brand} {model} {year} · fair price · {n} listings",
  "seo.year.description":
    "Fair price, p10/p90, and current best deals for the {year} {brand} {model}. {n} active listings in Europe.",
  "seo.year.h1": "Used {brand} {model} {year}",
  "seo.year.body": "{n} active listings for this model year. Observed median price: {median}.",
  "seo.year.deals.title": "Best {year} deals",
  "seo.year.deals.empty": "No listings for this model year as of the last refresh.",
  "seo.year.cta": "Watch continuously · free",

  "seo.country.title": "Used {brand} {model} in {country} · {n} listings",
  "seo.country.description":
    "{n} {brand} {model} for sale in {country}, median price {median}. Best deals refreshed hourly.",
  "seo.country.h1": "Used {brand} {model} in {country}",
  "seo.country.body": "{n} active listings. Observed median price: {median}.",
  "seo.country.deals.title": "Best deals · {country}",
  "seo.country.deals.empty": "No listings in this country as of the last refresh.",

  "seo.deals.col.deal": "Deal",
  "seo.deals.col.country": "Country",
  "seo.deals.col.listing": "Listing",
  "seo.deals.col.year": "Year",
  "seo.deals.col.km": "Km",
  "seo.deals.col.price": "Price",
  "seo.deals.col.savings": "Savings",
  "seo.deals.signinHint": "Sign in",

  "seo.404.title": "Page not found · MotorScan",
  "seo.404.description": "This page no longer exists, or hasn't been indexed (yet).",
  "seo.404.kicker": "404",
  "seo.404.h1": "Nothing here.",
  "seo.404.body":
    "This model or model year hasn't accumulated enough listings (yet) to deserve its own page. Start a scan to watch it continuously.",

  "country.fr": "France",
  "country.be": "Belgium",
  "country.ch": "Switzerland",
  "country.de": "Germany",
  "country.it": "Italy",
  "country.es": "Spain",
  "country.at": "Austria",
  "country.lu": "Luxembourg",
  "country.nl": "Netherlands",

  "og.kicker.market": "Market",
  "og.kicker.country": "Country",
  "og.kicker.year": "Year {year}",
  "og.kicker.brand": "MotorScan",
  "og.stat.listings": "Listings",
  "og.stat.median": "Median",
  "og.stat.deals": "Deals",
  "og.stat.sources": "Sources",
  "og.stat.countries": "Countries",
  "og.stat.freshness": "Freshness",
  "og.home.title": "The real-time observatory of the European used-car market.",
  "og.home.subtitle": "12 European sites aggregated. Refreshed hourly.",
  "og.model.subtitle":
    "{n} active listings in Europe. Median price {median}.",
};
